import { getI18nText } from '@/utils/commonFunc';

declare const bootstrap: any;

export interface ShowMsgUIOptions {
  /** 弹窗容器 id（页面默认是 modelShowMessageModal） */
  modalId?: string;
  /** 消息内容元素 id（页面默认是 modelShowMessageMessage） */
  messageId?: string;
}

/**
 * ShowMsgUI - 弹窗式消息通知组件
 * - 复用现有 `modelShowMessageModal` DOM 结构与样式类
 * - 暴露 showInfo(msg, durationSeconds) 用于显示通知
 */
export class ShowMsgUI {
  private modalId: string;
  private messageId: string;

  private modalDom: HTMLElement | null = null;
  private messageEl: HTMLElement | null = null;

  private modalInstance: any = null;
  private autoCloseTimer: number | NodeJS.Timeout | null = null;

  private handlersAttached: boolean = false;
  private onHidden?: () => void;

  /**
   * 让弹窗宽度自适应内容，但最多不超过页面宽度的 60%
   * - 这里按 viewport 宽度（60vw）作为“页面宽度”
   */
  private applyResponsiveWidth(): void {
    if (!this.modalDom) return;

    const dialog = this.modalDom.querySelector('.modal-dialog') as HTMLElement | null;
    const content = this.modalDom.querySelector('.vision-modal-content') as HTMLElement | null;

    const maxWidth = '60vw';

    if (dialog) {
      // 收缩到内容宽度，避免固定宽度导致的过宽问题
      dialog.style.width = 'fit-content';
      dialog.style.maxWidth = maxWidth;
    }

    if (content) {
      // 覆盖 modal-view.scss 里的 width:100%，让内容宽度可收缩
      content.style.width = 'fit-content';
      content.style.maxWidth = maxWidth;
    }
  }

  constructor(options: ShowMsgUIOptions = {}) {
    this.modalId = '_modelShowMessageModal';
    this.messageId = options.messageId ?? '_modelShowMessageMessage';

    this.ensureDom();
    this.cacheDomRefs();
    this.attachHandlersOnce();
  }

  private ensureDom(): void {
    // 如果页面已有默认 DOM，则直接复用
    this.modalDom = document.getElementById(this.modalId);
    if (this.modalDom) return;

    const modal = document.createElement('div');
    modal.className = 'modal fade vision-modal-bootstrap';
    modal.id = this.modalId;
    modal.setAttribute('tabindex', '-1');
    modal.setAttribute('aria-hidden', 'true');

    modal.innerHTML = `
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content vision-modal-content">
          <div class="modal-body vision-modal-body">
            <p class="vision-modal-message" id="${this.messageId}"></p>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modalDom = modal;
  }

  private cacheDomRefs(): void {
    if (!this.modalDom) return;

    this.messageEl = this.modalDom.querySelector(`#${this.messageId}`) as HTMLElement | null;
  }

  private attachHandlersOnce(): void {
    if (this.handlersAttached) return;
    if (!this.modalDom) return;

    // 当 modal 关闭时清理定时器，避免多次 show 叠加造成延迟关闭。
    this.onHidden = () => {
      if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer as any);
        this.autoCloseTimer = null;
      }
    };
    this.modalDom.addEventListener('hidden.bs.modal', this.onHidden as EventListener);

    this.handlersAttached = true;
  }

  /**
   * 显示消息通知
   * @param msg 需要显示的通知内容
   * @param durationSeconds 弹窗显示时间（单位：秒）；<= 0 表示不自动关闭
   */
  public showInfo(msg: string, durationSeconds: number = 0): void {
    this.ensureDom();
    this.cacheDomRefs();
    this.attachHandlersOnce();

    if (!this.modalDom || !this.messageEl) return;

    this.applyResponsiveWidth();

    // 清理上一次的自动关闭计时器
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer as any);
      this.autoCloseTimer = null;
    }

    const normalizedMsg = msg ?? '';
    this.messageEl.textContent = normalizedMsg;

    // 初始化 Bootstrap Modal 实例并显示
    if (!this.modalInstance && typeof bootstrap !== 'undefined' && bootstrap?.Modal) {
      this.modalInstance = new bootstrap.Modal(this.modalDom);
    }

    if (this.modalInstance && typeof this.modalInstance.show === 'function') {
      this.modalInstance.show();
    } else {
      // 兜底：没有 bootstrap 实例时用类名控制显示
      this.modalDom.classList.add('show', 'vision-modal-show');
      (this.modalDom as HTMLElement).style.display = 'block';
    }

    const duration = Number(durationSeconds);
    if (!Number.isNaN(duration) && duration > 0) {
      this.autoCloseTimer = setTimeout(() => {
        this.close();
      }, duration * 1000);
    }
  }

  /** 关闭弹窗（供内部与 自动关闭定时器调用） */
  public close(): void {
    if (!this.modalDom) return;

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer as any);
      this.autoCloseTimer = null;
    }

    if (this.modalInstance && typeof this.modalInstance.hide === 'function') {
      this.modalInstance.hide();
      return;
    }

    this.modalDom.classList.remove('show', 'vision-modal-show');
    (this.modalDom as HTMLElement).style.display = 'none';
  }
}

export function createShowMsgUI(options: ShowMsgUIOptions = {}): ShowMsgUI {
  return new ShowMsgUI(options);
}

